import { getDatabase, ref, get, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage, formatDateOnlyForStorage } from '../Helpers/DateUtils';
import { recordActivity } from './activityApi';

export class MaintenanceApi {
    private db = getDatabase();

    private getCurrentUser() {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }
        return user;
    }

    /**
     * Verify that the current user is an admin
     */
    private async verifyAdmin(): Promise<void> {
        const user = this.getCurrentUser();
        const adminRef = ref(this.db, `users/userList/${user.uid}/admin`);
        const adminSnapshot = await get(adminRef);
        if (!adminSnapshot.exists() || adminSnapshot.val() !== true) {
            throw new Error('Du måste vara administratör för att utföra denna åtgärd');
        }
    }

    // ============ SNOOZE OPERATIONS ============

    /**
     * Snooze a maintenance activity until a specific date (admin only)
     * - Sets snoozedUntil, snoozedBy, snoozedAt fields
     * - Activity will be hidden from active list until snoozedUntil date arrives
     */
    async snoozeMaintenance(
        buildingKey: string,
        componentKey: string,
        templateId: string,
        snoozeDate: string
    ): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        // Validate snooze date is in the future
        const today = formatDateOnlyForStorage(new Date());
        if (snoozeDate <= today) {
            throw new Error('Snooze-datum måste vara i framtiden');
        }

        const activityRef = ref(
            this.db,
            `maintenance/active/${buildingKey}/components/${componentKey}/maintenanceActivities/${templateId}`
        );
        const snapshot = await get(activityRef);

        if (!snapshot.exists()) {
            throw new Error('Underhållsaktivitet hittades inte');
        }

        await update(activityRef, {
            snoozedUntil: snoozeDate,
            snoozedBy: user.uid,
            snoozedAt: formatDateForStorage(new Date())
        });
        recordActivity('maintenancePlan.updated', { buildingKey, targetKey: componentKey });
    }

    /**
     * Unsnooze a maintenance activity, making it visible in the active list again (admin only)
     * - Clears snoozedUntil, snoozedBy, snoozedAt fields
     */
    async unsnoozeMaintenance(
        buildingKey: string,
        componentKey: string,
        templateId: string
    ): Promise<void> {
        await this.verifyAdmin();

        const activityRef = ref(
            this.db,
            `maintenance/active/${buildingKey}/components/${componentKey}/maintenanceActivities/${templateId}`
        );
        const snapshot = await get(activityRef);

        if (!snapshot.exists()) {
            throw new Error('Underhållsaktivitet hittades inte');
        }

        await update(activityRef, {
            snoozedUntil: null,
            snoozedBy: null,
            snoozedAt: null
        });
        recordActivity('maintenancePlan.updated', { buildingKey, targetKey: componentKey });
    }
}

// Export singleton instance
export const maintenanceApi = new MaintenanceApi();
